import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Device, type Call } from '@twilio/voice-sdk';

import { getTwilioToken } from '@services/Common';
import { useTwilioStore } from '@store/twilio/useTwilioStore';
import type { UseTwilioVoiceReturn, TwilioStatus } from '@types';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';

function toE164 (raw: string, defaultCountryCode = '+1'): string {
  const v = (raw ?? '').replace(/[^\d+]/g, '');
  if (v.length === 0) {
    return '';
  }
  if (v.startsWith('+')) {
    return v;
  }
  if (v.length === 10) {
    return `${defaultCountryCode}${v}`;
  }
  return v.startsWith('00') ? `+${v.slice(2)}` : `${defaultCountryCode}${v}`;
}

function looksLikePhoneNumber (value: string): boolean {
  return /^[+\d][\d\s-]+$/.test(value);
}

interface MaybeCallWithSid {
  parameters?: { CallSid?: string; Params?: string };
  customParameters?: { get?: (k: string) => string | undefined };
}

function extractCallSid (call: Call): string | null {
  const c = call as unknown as MaybeCallWithSid;
  return c.parameters?.CallSid ?? c.customParameters?.get?.('CallSid') ?? null;
}

function getErrMsg (err: unknown, fallback = 'Unexpected error'): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') {
      return m;
    }
  }
  return fallback;
}

const REGISTER_TIMEOUT_MS = 15000;

export function useTwilioVoice (identity?: string): UseTwilioVoiceReturn {
  const deviceRef = useRef<Device | null>(null);
  const connectionRef = useRef<Call | null>(null);
  const registerTimerRef = useRef<number | null>(null);

  const setStatus = useTwilioStore((s) => s.setStatus);
  const setActiveCall = useTwilioStore((s) => s.setActiveCall);
  const setCall = useTwilioStore((s) => s.setCall);
  const setIsMuted = useTwilioStore((s) => s.setIsMuted);
  const setCallSid = useTwilioStore((s) => s.setCallSid);
  const setConferenceName = useTwilioStore((s) => s.setConferenceName);

  const [status, setLocalStatus] = useState<TwilioStatus>('uninitialized');
  const [error, setError] = useState<string | null>(null);

  const updateStatus = (st: TwilioStatus): void => {
    setLocalStatus(st);
    setStatus(st);
  };

  useEffect(() => {
    let mounted = true;

    if (identity == null || identity.trim() === '') {
      void logWarning('Missing identity, skipping Twilio init', 'useTwilioVoice/init', { identity: String(identity ?? '') }, false);
      updateStatus('offline');
      return;
    }

    void (async (): Promise<void> => {
      try {
        updateStatus('registering');
        setError(null);

        const { data, error: svcErr } = await getTwilioToken(identity);
        if (svcErr != null || data?.token == null) {
          throw new Error(svcErr?.message ?? 'Error getting Twilio token');
        }

        void logInfo('Twilio token obtained', 'useTwilioVoice/token', { hasIdentity: true }, false);

        const dev = new Device(data.token, { logLevel: 1 });
        deviceRef.current = dev;

        const markReady = (): void => {
          if (!mounted) {
            return;
          }
          updateStatus('ready');
          void logInfo('Twilio ready', 'useTwilioVoice/ready', {}, false);
        };

        const clearRegisterTimer = (): void => {
          if (registerTimerRef.current != null) {
            window.clearTimeout(registerTimerRef.current);
            registerTimerRef.current = null;
          }
        };

        dev.on('registered', () => {
          clearRegisterTimer();
          markReady();
        });

        dev.on('ready', () => {
          clearRegisterTimer();
          markReady();
        });

        dev.on('incoming', (call: Call) => {
          // Extract conference name from ALL incoming calls (duplicates may carry it too)
          const callWithParams = call as unknown as MaybeCallWithSid;
          let confName: string | null = null;
          try {
            const paramsStr = callWithParams.parameters?.Params ?? '';
            if (paramsStr !== '') {
              const parsed = new URLSearchParams(paramsStr);
              confName = parsed.get('conferenceFriendlyName');
            }
          } catch {
            // ignore parse errors
          }
          if (confName == null || confName === '') {
            confName = callWithParams.customParameters?.get?.('conferenceFriendlyName') ?? null;
          }
          if (confName != null && confName !== '') {
            setConferenceName(confName);
          }

          // Guard: if there's already an active call, this is a duplicate incoming event.
          // Do NOT accept (avoids second audio path) and do NOT reject (avoids killing
          // a legitimate conference leg). The ignored call will time out on Twilio's side.
          const existingCall = useTwilioStore.getState().activeCall;
          if (existingCall != null) {
            void logInfo('Duplicate incoming call, ignoring', 'useTwilioVoice/incoming', { conferenceName: confName }, false);
            return;
          }

          connectionRef.current = call;
          setActiveCall(true);
          setCall(call);
          setIsMuted(false);

          void logInfo('Incoming call', 'useTwilioVoice/incoming', { conferenceName: confName }, false);

          try {
            call.accept();
          } catch (e) {
            void logWarning(getErrMsg(e, 'Auto-accept failed'), 'useTwilioVoice/incoming/accept', {}, false);
          }

          call.on('accept', () => {
            const sid = extractCallSid(call);
            if (sid != null) {
              setCallSid(sid);
            }
            void logInfo('Incoming call accepted', 'useTwilioVoice/incoming/accepted', {}, false);
          });

          const resetIncoming = (): void => {
            // Only reset store if THIS call is still the active one.
            // Prevents duplicate incoming calls from wiping state of the real call.
            const current = useTwilioStore.getState().activeCall;
            if (current != null && current !== call) {
              void logInfo('Skipping reset for non-active call', 'useTwilioVoice/incoming/reset', {}, false);
              return;
            }
            setActiveCall(false);
            setCall(null);
            setIsMuted(false);
            setCallSid(null);
            // Cleared here too. endCall() already did it, but a leg that simply
            // disconnects never goes through endCall, and leaving a dead
            // conference name in the store is what the transfer detection keys on.
            setConferenceName(null);
            updateStatus('ready');
            void logInfo('Incoming call ended', 'useTwilioVoice/incoming/reset', {}, false);
          };

          call.on('disconnect', resetIncoming);
          call.on('cancel', resetIncoming);
          call.on('reject', resetIncoming);
          call.on('error', resetIncoming);

          // Sync mute state with Twilio SDK
          call.on('mute', (isMuted: boolean) => {
            setIsMuted(isMuted);
            void logInfo(isMuted ? 'Call muted (SDK event)' : 'Call unmuted (SDK event)', 'useTwilioVoice/incoming/mute', {}, false);
          });
        });

        dev.on('error', (e: unknown) => {
          if (!mounted) {
            return;
          }
          const msg = getErrMsg(e, 'Device error');
          // Twilio error 31000 is a generic transient error — log but don't show toast
          const errCode = typeof e === 'object' && e !== null && 'code' in e
            ? (e as { code?: number }).code
            : undefined;
          if (errCode === 31000) {
            void logWarning(`Twilio transient error: ${msg}`, 'useTwilioVoice/deviceError', { code: errCode }, false);
            return;
          }
          setError(msg);
          updateStatus('error');
          void logError(msg, 'useTwilioVoice/deviceError', { status }, true, false);
        });

        registerTimerRef.current = window.setTimeout(() => {
          if (!mounted) {
            return;
          }
          const msg = 'Twilio register timeout';
          setError(msg);
          updateStatus('error');
          void logError(msg, 'useTwilioVoice/registerTimeout', { timeoutMs: REGISTER_TIMEOUT_MS }, false, false);
        }, REGISTER_TIMEOUT_MS);

        void dev.register().catch((e) => {
          clearRegisterTimer();
          if (!mounted) {
            return;
          }
          const msg = getErrMsg(e, 'Device register failed');
          setError(msg);
          updateStatus('error');
          void logError(msg, 'useTwilioVoice/register', {}, true, false);
        });
      } catch (e) {
        if (!mounted) {
          return;
        }
        const msg = getErrMsg(e, 'Setup error');
        setError(msg);
        updateStatus('error');
        void logError(msg, 'useTwilioVoice/setup', {}, true, false);
      }
    })();

    return () => {
      mounted = false;

      if (registerTimerRef.current != null) {
        window.clearTimeout(registerTimerRef.current);
        registerTimerRef.current = null;
      }

      try {
        connectionRef.current?.disconnect();
      } catch (e) {
        void logWarning(getErrMsg(e, 'Disconnect current call failed'), 'useTwilioVoice/cleanup/disconnect', {}, false);
      }

      try {
        deviceRef.current?.disconnectAll();
        deviceRef.current?.destroy();
      } catch (e) {
        void logWarning(getErrMsg(e, 'Destroy device failed'), 'useTwilioVoice/cleanup/device', {}, false);
      }

      connectionRef.current = null;
      deviceRef.current = null;

      setActiveCall(false);
      setCall(null);
      setIsMuted(false);
      setCallSid(null);
      setConferenceName(null);
      updateStatus('offline');

      void logInfo('Cleanup completed', 'useTwilioVoice/cleanup', {}, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const startCall: UseTwilioVoiceReturn['startCall'] = async (toNumber, params = {}) => {
    const dev = deviceRef.current;
    if (dev == null) {
      throw new Error('Twilio Device not ready');
    }
    if (toNumber.trim() === '') {
      throw new Error('Missing destination');
    }

    updateStatus('busy');

    const destination = looksLikePhoneNumber(toNumber)
      ? toE164(toNumber, '+1')
      : toNumber;

    const strParams: Record<string, string> = {};
    Object.entries(params).forEach(([k, v]) => {
      strParams[k] = String(v);
    });

    try {
      const call = await dev.connect({
        params: {
          number: destination,
          To: destination,
          ...strParams,
        },
      });

      connectionRef.current = call;
      setCall(call);
      setActiveCall(true);
      setIsMuted(false);

      void logInfo('Outbound call started', 'useTwilioVoice/startCall', { destination }, false);

      call.on('accept', () => {
        const sid = extractCallSid(call);
        if (sid != null) {
          setCallSid(sid);
        }
        void logInfo('Outbound call accepted', 'useTwilioVoice/startCall/accepted', {}, false);
      });

      const reset = (): void => {
        if (connectionRef.current === call) {
          connectionRef.current = null;
        }
        setActiveCall(false);
        setCall(null);
        setIsMuted(false);
        setCallSid(null);
        updateStatus('ready');
        void logInfo('Outbound call ended', 'useTwilioVoice/startCall/reset', {}, false);
      };

      call.on('disconnect', reset);
      call.on('cancel', reset);
      call.on('reject', reset);
      call.on('error', reset);

      // Sync mute state with Twilio SDK
      call.on('mute', (isMuted: boolean) => {
        setIsMuted(isMuted);
        void logInfo(isMuted ? 'Call muted (SDK event)' : 'Call unmuted (SDK event)', 'useTwilioVoice/startCall/mute', {}, false);
      });
    } catch (e) {
      const msg = getErrMsg(e, 'Connect error');
      setError(msg);
      updateStatus('ready');
      void logError(msg, 'useTwilioVoice/startCall/error', { destination }, true, false);
      throw e;
    }
  };

  // Keep the latest endCall impl in a ref so the stable wrapper always calls current logic
  const endCallImplRef = useRef<UseTwilioVoiceReturn['endCall']>(() => {});
  endCallImplRef.current = () => {
    try {
      connectionRef.current?.disconnect();
      void logInfo('End call requested', 'useTwilioVoice/endCall', {}, false);
    } catch (e) {
      void logWarning(getErrMsg(e, 'End call failed'), 'useTwilioVoice/endCall', {}, false);
    }

    try {
      deviceRef.current?.disconnectAll();
      void logInfo('Disconnect all requested', 'useTwilioVoice/endCall/disconnectAll', {}, false);
    } catch (e) {
      void logWarning(getErrMsg(e, 'Disconnect all failed'), 'useTwilioVoice/endCall/disconnectAll', {}, false);
    }

    connectionRef.current = null;
    setActiveCall(false);
    setCall(null);
    setIsMuted(false);
    setCallSid(null);
    setConferenceName(null);
    updateStatus('ready');
  };

  // Stable reference that never changes — avoids unnecessary effect restarts in consumers
  const endCall = useCallback<UseTwilioVoiceReturn['endCall']>(() => {
    endCallImplRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendDigits: UseTwilioVoiceReturn['sendDigits'] = (digits) => {
    try {
      connectionRef.current?.sendDigits(digits);
      void logInfo('DTMF sent', 'useTwilioVoice/sendDigits', { digits }, false);
    } catch (e) {
      void logWarning(getErrMsg(e, 'Send DTMF failed'), 'useTwilioVoice/sendDigits', { digits }, false);
    }
  };

  const currentCall: UseTwilioVoiceReturn['currentCall'] = () => connectionRef.current;

  return useMemo(
    () => ({ status, error, startCall, endCall, sendDigits, currentCall }),
    [status, error],
  );
}
